import React, { useMemo, useState, useRef } from 'react';
import { View, Text, StyleSheet, PanResponder, LayoutChangeEvent } from 'react-native';
import Svg, { Path, Circle, Line, Defs, LinearGradient, Stop } from 'react-native-svg';

export interface MetaChartPoint {
  date: string;
  views: number;
  /** Count of posts published that day — rendered as the spiky white
   * "Publishing activity" overlay line in Meta's own Insights chart. */
  posts: number;
}

export interface MetaInsightsChartProps {
  data: MetaChartPoint[];
  height?: number;
  showPublishingActivity: boolean;
}

function linePath(points: { x: number; y: number }[]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
}

/** Replicates Meta's own Page Insights chart panel exactly: a blue filled
 * area for the primary metric (views), a spiky white overlay line for
 * "Publishing activity" (each post = a sharp triangular spike, since that's
 * literally how Meta renders a same-day publish-count series — not a
 * smoothed curve), horizontal gridlines, and independent left/right y-axis
 * scales (left = views' own scale, right = a small 0/1 scale for the
 * publishing-activity line, matching the screenshot). Always rendered on a
 * fixed dark canvas — Meta's Insights dashboard doesn't adapt to the host
 * app's light/dark theme, it's a fixed dark tool regardless of context. */
export const MetaInsightsChart: React.FC<MetaInsightsChartProps> = ({ data, height = 260, showPublishingActivity }) => {
  const [width, setWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const widthRef = useRef(0);

  const PADDING_LEFT = 56;
  const PADDING_RIGHT = 32;
  const PADDING_TOP = 16;
  const PADDING_BOTTOM = 28;

  const onLayout = (e: LayoutChangeEvent) => {
    widthRef.current = e.nativeEvent.layout.width;
    setWidth(e.nativeEvent.layout.width);
  };

  const chart = useMemo(() => {
    if (data.length === 0 || width === 0) return null;
    const plotWidth = width - PADDING_LEFT - PADDING_RIGHT;
    const plotHeight = height - PADDING_TOP - PADDING_BOTTOM;
    const maxViews = Math.max(...data.map((d) => d.views), 1);
    const maxPosts = Math.max(...data.map((d) => d.posts), 1);
    const step = data.length > 1 ? plotWidth / (data.length - 1) : 0;

    const viewPoints = data.map((d, i) => ({
      x: PADDING_LEFT + i * step,
      y: PADDING_TOP + plotHeight - (d.views / maxViews) * plotHeight,
    }));
    const postPoints = data.map((d, i) => ({
      x: PADDING_LEFT + i * step,
      y: PADDING_TOP + plotHeight - (d.posts / maxPosts) * plotHeight,
    }));

    const viewsLine = linePath(viewPoints);
    const viewsFill = `${viewsLine} L ${viewPoints[viewPoints.length - 1].x},${PADDING_TOP + plotHeight} L ${viewPoints[0].x},${PADDING_TOP + plotHeight} Z`;
    const postsLine = linePath(postPoints);

    // Gridlines + left-axis labels at 0 / half / max of the views scale.
    const gridLines = [0, 0.5, 1].map((frac) => ({
      y: PADDING_TOP + plotHeight - frac * plotHeight,
      label: Math.round(maxViews * frac).toLocaleString(),
    }));

    // Date ticks at first/mid/last, matching the screenshot's every-~5-days feel.
    const tickCount = Math.min(5, data.length);
    const tickIndices = Array.from({ length: tickCount }, (_, i) =>
      Math.round((i * (data.length - 1)) / Math.max(tickCount - 1, 1))
    );
    const dateTicks = Array.from(new Set(tickIndices)).map((i) => ({ x: viewPoints[i].x, label: data[i].date }));

    return { viewPoints, postPoints, viewsLine, viewsFill, postsLine, gridLines, dateTicks, plotHeight, plotWidth, maxPosts };
  }, [data, width, height]);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => !!chart,
      onMoveShouldSetPanResponder: () => !!chart,
      onPanResponderMove: (evt) => {
        if (!chart || widthRef.current === 0) return;
        const relativeX = Math.max(0, Math.min(widthRef.current, evt.nativeEvent.locationX));
        let nearest = 0;
        let nearestDist = Infinity;
        chart.viewPoints.forEach((p, i) => {
          const dist = Math.abs(p.x - relativeX);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        });
        setActiveIndex(nearest);
      },
      onPanResponderRelease: () => setActiveIndex(null),
      onPanResponderTerminate: () => setActiveIndex(null),
    })
  ).current;

  const gradientId = useRef(`metaChartFill-${Math.random().toString(36).slice(2)}`).current;
  const activePoint = activeIndex != null ? data[activeIndex] : null;
  const activeViewXY = activeIndex != null && chart ? chart.viewPoints[activeIndex] : null;

  return (
    <View>
      <View style={{ height }} onLayout={onLayout} {...panResponder.panHandlers}>
        {chart ? (
          <Svg width={width} height={height}>
            <Defs>
              <LinearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor="#1877F2" stopOpacity={0.55} />
                <Stop offset="1" stopColor="#1877F2" stopOpacity={0.04} />
              </LinearGradient>
            </Defs>

            {chart.gridLines.map((g, i) => (
              <React.Fragment key={i}>
                <Line x1={PADDING_LEFT} y1={g.y} x2={width - PADDING_RIGHT} y2={g.y} stroke="#3A3B3C" strokeWidth={1} />
                <Text style={[styles.axisLabelLeftAbs, { top: g.y - 7 }]}>{g.label}</Text>
              </React.Fragment>
            ))}

            <Path d={chart.viewsFill} fill={`url(#${gradientId})`} />
            <Path d={chart.viewsLine} fill="none" stroke="#4599FF" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />

            {showPublishingActivity && (
              <Path d={chart.postsLine} fill="none" stroke="#FFFFFF" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
            )}

            {activeViewXY && (
              <>
                <Line x1={activeViewXY.x} y1={PADDING_TOP} x2={activeViewXY.x} y2={PADDING_TOP + chart.plotHeight} stroke="#5A5B5C" strokeWidth={1} strokeDasharray="4 4" />
                <Circle cx={activeViewXY.x} cy={activeViewXY.y} r={5} fill="#4599FF" stroke="#18191A" strokeWidth={2} />
              </>
            )}
          </Svg>
        ) : (
          <View style={styles.empty}><Text style={styles.emptyText}>No data yet</Text></View>
        )}

        {/* Right-axis label — a small fixed 0/1 scale for the publishing-
            activity overlay, matching the screenshot's minimal right axis. */}
        {chart && showPublishingActivity && (
          <>
            <Text style={[styles.axisLabelRightAbs, { top: PADDING_TOP - 7, right: 4 }]}>{chart.maxPosts}</Text>
            <Text style={[styles.axisLabelRightAbs, { top: PADDING_TOP + chart.plotHeight - 7, right: 4 }]}>0</Text>
          </>
        )}
      </View>

      {chart && (
        <View style={[styles.dateAxisRow, { paddingLeft: PADDING_LEFT, paddingRight: PADDING_RIGHT }]}>
          {chart.dateTicks.map((tick, i) => (
            <Text key={`${tick.label}-${i}`} style={styles.dateAxisLabel}>{tick.label}</Text>
          ))}
        </View>
      )}

      {activePoint && (
        <Text style={styles.tooltip}>{activePoint.date} · {activePoint.views.toLocaleString()} views</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: '#B0B3B8',
  },
  axisLabelLeftAbs: {
    position: 'absolute',
    left: 0,
    fontSize: 11,
    color: '#B0B3B8',
  },
  axisLabelRightAbs: {
    position: 'absolute',
    fontSize: 11,
    color: '#B0B3B8',
  },
  dateAxisRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 6,
  },
  dateAxisLabel: {
    fontSize: 11,
    color: '#B0B3B8',
  },
  tooltip: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: '600' as const,
    color: '#E4E6EB',
    textAlign: 'center',
  },
});
